import { GetInspectionsRequestDTO } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const inspectionKeys = {
  all: ['inspections'] as const,
  detail: (inspectionId: string | undefined, isAWA: boolean) => [...inspectionKeys.all, 'detail', inspectionId, isAWA] as const,
  revisions: (inspectionId: string | undefined, isAWA: boolean) => [...inspectionKeys.all, 'revisions', inspectionId, isAWA] as const,
  formByRevisionId: (formRevisionId: string | null | undefined, isAWA: boolean) => [...inspectionKeys.all, 'form', formRevisionId, isAWA] as const,
  latestByAsset: (assetId: string | undefined) => [...inspectionKeys.all, 'latest', assetId] as const,
  byAssetForm: (assetId: string, formId: string) => [...inspectionKeys.all, 'by-asset-form', assetId, formId] as const,
  infinite: (filters?: GetInspectionsRequestDTO['filters'], groupBy?: GetInspectionsRequestDTO['groupBy'], orderBy?: GetInspectionsRequestDTO['orderBy']) =>
    [...inspectionKeys.all, 'infinite', filters, groupBy, orderBy] as const,
  byAssetInfinite: (
    assetId: string | undefined,
    filters?: GetInspectionsRequestDTO['filters'],
    groupBy?: GetInspectionsRequestDTO['groupBy'],
    orderBy?: GetInspectionsRequestDTO['orderBy'],
  ) => [...inspectionKeys.all, 'by-asset-infinite', assetId, filters, groupBy, orderBy] as const,
};

export const activityKeys = {
  all: ['activity'] as const,
  byAsset: (assetId: string) => [...activityKeys.all, 'asset', assetId] as const,
};

export const getInspectionByIdQueryOptions = ({ inspectionId, isAWA = false }: { inspectionId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: inspectionKeys.detail(inspectionId, isAWA),
    queryFn: async () => {
      if (!inspectionId) return null;
      return await (await (isAWA ? AWAApiV2Service : ApiService).GetInspectionById({ inspectionId })).json();
    },
    enabled: !!inspectionId,
  });
};

export const getInspectionRevisionsQueryOptions = ({ inspectionId, isAWA = false }: { inspectionId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: inspectionKeys.revisions(inspectionId, isAWA),
    queryFn: async () => {
      if (!inspectionId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetInspectionRevisions({ inspectionId })).json()).revisions;
    },
    enabled: !!inspectionId,
  });
};

export const getInspectionFormQueryOptions = ({ formRevisionId, isAWA = false }: { formRevisionId: string | null | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: inspectionKeys.formByRevisionId(formRevisionId, isAWA),
    queryFn: async () => {
      if (!formRevisionId) return null;
      return await (await (isAWA ? AWAApiV2Service : ApiService).GetFormRevisionById({ formRevisionId })).json();
    },
    enabled: !!formRevisionId,
  });
};

export const getLatestInspectionQueryOptions = ({ assetId }: { assetId: string | undefined }) => {
  return queryOptions({
    queryKey: inspectionKeys.latestByAsset(assetId),
    queryFn: async () => {
      if (!assetId) throw new Error('Asset ID is required');
      return (await AWAApiV2Service.GetLatestInspectionByAsset({ assetId })).json();
    },
    enabled: !!assetId,
  });
};

export const getAssetFormInspectionsQueryOptions = ({ assetId, formId }: { assetId: string; formId: string }) => {
  return queryOptions({
    queryKey: inspectionKeys.byAssetForm(assetId, formId),
    queryFn: async () => {
      const { data: forms } = await (
        await AWAApiV2Service.GetInspections({
          filters: {
            fields: {
              assetId: [assetId],
              formId: [formId],
            },
          },
        })
      ).json();
      return forms;
    },
  });
};

export const getInspectionsInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetInspectionsRequestDTO['filters'];
  groupBy?: GetInspectionsRequestDTO['groupBy'];
  orderBy?: GetInspectionsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: inspectionKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetInspections({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getActivityByAssetQueryOptions = ({ assetId }: { assetId: string }) => {
  return queryOptions({
    queryKey: activityKeys.byAsset(assetId),
    queryFn: async () => {
      const { data: activity } = await (await AWAApiV2Service.GetActivityByAsset({ assetId })).json();
      return activity;
    },
  });
};
